import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-form';
import moment from 'moment';
import PropTypes, { any } from 'prop-types';

import { addDivisionSteps, combineDivisions, areScoreFormatPrioritiesValid, divisionScoringFormatKeyMap, playOffDrawDetails, scoringTypeOverrides, getDateTimeObjectFromCurrentDateAndTime, primaryViews } from '../../../utils/dayPlanner';
import { getDivisionsAndVirtualDraws, getScoringFormats } from '../../../api/tmsEventsDayPlannerApi';
import { getApiAxiosInstance } from '../../../utils/network';
import DayPlannerSidePanel from './DayPlannerSidePanel';
import { CheckBox, Input, Select } from '../../../components/Form';
import { dateFormat, timeFormatForDayConfig } from '../../../utils/courtSchedular';
import SelectAllCheckBox from './SelectAllCheckBox';
import Popup from '../../../components/Popup';
import { drawEliminationTypeIds, drawEliminationLabels, sportTypeIds } from '../../../utils/eventConstants';
import { getAllowedScoringFormats, getDefaultAllottedTimeForScoringType, EventTypes } from '../../../utils/events';
import { allowedMatchTimes } from '../../../utils/allottedTimeConstants';
import { saveVirtualDraws } from '../../../actions/day-planner-actions';
import { required } from '../../../utils/form-validation';
import getOptionsForTimeSelect from '../../../components/Form/utils';

import styles from './dayPlanner.scss';

const AddDivisionsSidePanel = (props) => {
    const {
        className,
        closeSidePanel,
        eventId,
        sportTypeId,
        eventTypeId,
        eventSchedule,
        eventAllottedMatchTimes,
    } = props;

    const dispatch = useDispatch();
    const dayPlanner = useSelector(({ dayPlanner }) => dayPlanner);
    const coreUI = useSelector(({ coreUI }) => coreUI);

    const {
        division: {
            tournament: {
                drawEliminationTypes,
                scoringTypes,
            },
        },
    } = coreUI.types.tms;

    const [data, setData] = useState(null);
    const [step, setStep] = useState(addDivisionSteps.SELECT_DIVISIONS);
    const [combinedDivisionState, setCombinedDivisionState] = useState({
        needsConfirmation: false,
    });

    const selectableScoringTypes = scoringTypes.filter(type => {
        return type.sportTypeId === sportTypeId;
    });

    const getSelectableScoringFormats = (values) => {
        if (sportTypeId === sportTypeIds.PICKLEBALL) {
            return selectableScoringTypes;
        }

        return getAllowedScoringFormats(selectableScoringTypes, eventTypeId, values.formatTypeId, values.ballTypeId);
    };

    const startTimeOptions = useMemo(() => {
        const dayStartTime = dayPlanner?.dayConfig?.startTime;
        const timeSelectOptions = getOptionsForTimeSelect(15);

        if (dayStartTime) {
            const { timeZone } = eventSchedule;
            const startTime = moment.utc(dayStartTime).tz(timeZone?.value).format(timeFormatForDayConfig);
            const startTimeIndex = timeSelectOptions.findIndex((option) => option.value === startTime);
            if (startTimeIndex >= 0) {
                timeSelectOptions.splice(0, startTimeIndex);
            }
        }

        return timeSelectOptions;
    }, [dayPlanner.dayConfig, eventSchedule]);

    const defaultValues = useMemo(() => {
        if (data) {
            const virtualDraws = data.divisionsAndVirtualDraws.virtualDraws.map((vd) => {
                return {
                    ...vd,
                    selected: !!vd.virtualDrawId,
                    disableDrawLevelChanges: !!vd.virtualDrawId,
                    divisionIds: [vd.divisionId],
                    ...(vd.startTime && {
                        startTime: moment.utc(vd.startTime).tz(eventSchedule?.timeZone?.value).format(timeFormatForDayConfig),
                    }),
                };
            });

            const formatKeys = data.scoringFormats ? Object.keys(data.scoringFormats) : [];
            const scoringFormats = formatKeys.map((key) => {
                const elimType = drawEliminationTypes.find((type) => type.value === divisionScoringFormatKeyMap[key]);
                return {
                    key,
                    value: divisionScoringFormatKeyMap[key],
                    label: elimType?.label,
                    ...data.scoringFormats[key],
                    shouldSplitIntoGroups: !!data.scoringFormats[key]?.splitGroupsSize && !!data.scoringFormats[key]?.splitGroupsThreshold,
                    eliminationTypeId: elimType.id,
                };
            });

            return {
                virtualDraws,
                scoringFormats,
            };
        }

        return null;
    }, [data, eventSchedule]);

    useEffect(() => {
        const getData = async () => {
            const { primaryView, facilityId } = dayPlanner.viewState;
            const divisionsResponse = await getDivisionsAndVirtualDraws(getApiAxiosInstance(), eventId, primaryView, dayPlanner.dayConfig.id, facilityId);
            if (primaryView === primaryViews.CapacitySimulator) {
                const scoringFormatsResponse = await getScoringFormats(getApiAxiosInstance(), eventId);
                setData({
                    divisionsAndVirtualDraws: divisionsResponse.data,
                    scoringFormats: scoringFormatsResponse.data,
                });
            } else {
                setData({
                    divisionsAndVirtualDraws: divisionsResponse.data,
                });
            }
        };

        if (!data) {
            getData();
        }
    }, [data, eventId, eventSchedule]);

    const onClickAddFormatRules = () => {
        setStep(addDivisionSteps.DEFINE_SCORING_FORMATS);
    };

    const onCloseConfirmCombine = () => {
        setCombinedDivisionState({
            needsConfirmation: false,
        });
    };

    const onConfirmCombine = (formApi) => () => {
        setCombinedDivisionState({
            needsConfirmation: false,
            eliminationTypeId: combinedDivisionState.eliminationTypeId,
        });

        combineSelectedDivisions(formApi)();
    };

    const onChangeCombinedFormatOption = (eliminationTypeId) => {
        setCombinedDivisionState({
            needsConfirmation: combinedDivisionState.needsConfirmation,
            eliminationTypeId,
        });
    };

    const onFormSubmit = async (values) => {
        const selectedDraws = values.virtualDraws.filter((vd) => vd.selected);
        const virtualDrawsToUpdate = selectedDraws.filter((vd) => vd.virtualDrawId);
        const virtualDrawsToCreate = selectedDraws.filter((vd) => !vd.virtualDrawId);

        const scoreFormatOptions = values.scoringFormats.reduce((acc, scoreFormat) => {
            const { key, label, values, ...otherScoringFormats } = scoreFormat;
            return {
                ...acc,
                [key]: {
                    ...otherScoringFormats,
                },
            };
        }, {});

        let updatedValues = {};
        if (dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator) {
            updatedValues = {
                updates: virtualDrawsToUpdate.map((vd) => {
                    return {
                        virtualDrawId: vd.virtualDrawId,
                        priority: vd.priority,
                        entrants: vd.entrants,
                        maxCourts: vd.maxCourts,
                    };
                }),
                creates: virtualDrawsToCreate.map((vd) => {
                    return {
                        eliminationTypeId: vd.eliminationTypeId,
                        startTime: getDateTimeObjectFromCurrentDateAndTime(dayPlanner.dayConfig.day, vd.startTime, eventSchedule),
                        priority: vd.priority,
                        entrants: vd.entrants,
                        maxCourts: vd.maxCourts,
                        divisionIds: vd.divisionIds,
                    };
                }),
                options: scoreFormatOptions,
            };
        } else if (dayPlanner.viewState.primaryView === primaryViews.DraftDraws) {
            updatedValues = {
                updates: virtualDrawsToUpdate.map((vd) => {
                    return {
                        virtualDrawId: vd.virtualDrawId,
                        priority: vd.priority,
                        entrants: vd.entrants,
                        maxCourts: vd.maxCourts,
                    };
                }),
                creates: virtualDrawsToCreate.map((vd) => {
                    return {
                        drawId: vd.drawId,
                        startTime: getDateTimeObjectFromCurrentDateAndTime(dayPlanner.dayConfig.day, vd.startTime, eventSchedule),
                        priority: vd.priority,
                        entrants: vd.entrants,
                        maxCourts: vd.maxCourts,
                    };
                }),
                options: scoreFormatOptions,
            };
        }

        dispatch(saveVirtualDraws(updatedValues));
    };

    const combineSelectedDivisions = (formApi) => () => {
        const virtualDrawsOrDivisions = formApi.getValue('virtualDraws');
        const positionOfFIrstSelectedDivision = virtualDrawsOrDivisions.findIndex((vd) => vd.selected && !vd.disableDrawLevelChanges);
        const selectedDivisions = virtualDrawsOrDivisions.filter((vd) => vd.selected && !vd.disableDrawLevelChanges);

        if (!combinedDivisionState.eliminationTypeId) {
            setCombinedDivisionState({
                needsConfirmation: true,
                eliminationTypeId: selectedDivisions[0].eliminationTypeId,
            });
            return;
        }

        const unSelectedDivisions = virtualDrawsOrDivisions.filter((vd) => !vd.selected || vd.disableDrawLevelChanges);

        const combinedDiv = combineDivisions(selectedDivisions, combinedDivisionState.eliminationTypeId);

        if (positionOfFIrstSelectedDivision < unSelectedDivisions.length) {
            unSelectedDivisions.splice(positionOfFIrstSelectedDivision, 0, combinedDiv);
        } else {
            unSelectedDivisions.push(combinedDiv);
        }

        formApi.setValue('virtualDraws', unSelectedDivisions);
        setCombinedDivisionState({
            needsConfirmation: false,
        });
    };

    const onClearAllDivisionSelections = (formApi) => () => {
        const virtualDrawsOrDivisions = formApi.getValue('virtualDraws');
        virtualDrawsOrDivisions.forEach((vd, i) => {
            if (!vd.disableDrawLevelChanges) {
                formApi.setValue(['virtualDraws', i, 'selected'], false);
            }
        });
    };

    const onScoringFormatChange = (formApi, formatIndex, allotedMatchTimeFieldName) => (scoringTypeId) => {
        const allottedTime = getDefaultAllottedTimeForScoringType(scoringTypeId, eventAllottedMatchTimes, scoringTypes);
        formApi.setValue(['scoringFormats', formatIndex, allotedMatchTimeFieldName], allottedTime);
    };

    const onSelectAllDivisions = (formApi) => () => {
        const virtualDrawsOrDivisions = formApi.getValue('virtualDraws');
        virtualDrawsOrDivisions.forEach((vd, i) => {
            if (!vd.disableDrawLevelChanges) {
                formApi.setValue(['virtualDraws', i, 'selected'], true);
            }
        });
    };

    const onCancelClick = () => {
        if (step === addDivisionSteps.DEFINE_SCORING_FORMATS) {
            setStep(addDivisionSteps.SELECT_DIVISIONS);
        } else {
            closeSidePanel();
        }
    };

    const onChangeScoreFormatOptionId = (formApi, vdIndex) => (eliminationTypeId) => {
        const selectedDrawFormatOption = scoreFormatOptions.find((sf) => sf.eliminationTypeId === eliminationTypeId);

        if (selectedDrawFormatOption) {
            formApi.setValue(['virtualDraws', vdIndex, 'eliminationTypeId'], selectedDrawFormatOption.eliminationTypeId);
        }
    };

    const maxCourtOptions = useMemo(() => {
        if (data?.divisionsAndVirtualDraws) {
            return data.divisionsAndVirtualDraws.maxCourtsOptions.filter((mcO) => mcO.maxCourts);
        }

        return [];
    }, [data]);

    const scoreFormatOptions = useMemo(() => {
        return data?.divisionsAndVirtualDraws?.scoreFormatOptions;
    }, [data]);

    if (!defaultValues) {
        return null;
    }

    const getDrawEliminationTypeLabel = (elimTypeId) => {
        const detKey = Object.keys(drawEliminationTypeIds).find(k => drawEliminationTypeIds[k] === elimTypeId);
        return drawEliminationLabels[detKey];
    };

    const genRowKey = (row, i) => {
        if (row.virtualDrawId) { return `virtual-draw-${row.virtualDrawId}`; }
        if (row.divisionId) { return `candidate-division-${row.divisionId}`; }
        if (row.drawId) { return `candidate-draw-${row.drawId}`; }
        return `unknown-${i}`;
    };

    return (
        <Form
            onSubmit={onFormSubmit}
            defaultValues={defaultValues}
        >
            {(formApi) => {
                const { submitForm, values } = formApi;

                const allSelectedDivisions = values.virtualDraws.filter((vd) => vd.selected);
                const newSelectedDivisions = allSelectedDivisions.filter((vd) => !vd.disableDrawLevelChanges);
                const canCombineDivisions = dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator && newSelectedDivisions.length > 1;
                const areValidScoreFormatPriorities = areScoreFormatPrioritiesValid(values?.virtualDraws);
                const canContinueFromSelections = allSelectedDivisions.length > 0 && areValidScoreFormatPriorities;

                return (
                    <>
                        <form onSubmit={submitForm} id="dayConfigForm" data-testid="day-planner.add-divisions.form">
                            <DayPlannerSidePanel
                                className={className}
                                closeSideBar={closeSidePanel}
                                data-testid="day-planner.add-divisions.panel"
                            >
                                <div className={styles.header} data-testid="day-planner.add-divisions.header">
                                    {step === addDivisionSteps.SELECT_DIVISIONS && `Add ${dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator ? 'divisions' : 'draws'}`}
                                    {step === addDivisionSteps.DEFINE_SCORING_FORMATS && 'Define scoring format rules'}
                                </div>
                                {step === addDivisionSteps.SELECT_DIVISIONS && (
                                    <div className={`${styles.content} ${styles.selectDivisions}`}>
                                        {values.virtualDraws?.length > 0 && (
                                            <>
                                                {dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator && (
                                                    <p className={`mb0 ${styles.notice}`} data-testid="day-planner.add-divisions.notice">
                                                        Choose which divisions you’d like to auto-schedule.
                                                        Note: Schedules with divisions cannot be published or started.
                                                    </p>
                                                )}
                                                {dayPlanner.viewState.primaryView === primaryViews.DraftDraws && (
                                                    <p className={`mb0 ${styles.notice}`} data-testid="day-planner.add-divisions.notice">
                                                        Choose which draws you’d like to auto-schedule.
                                                    </p>
                                                )}
                                                <div className={styles.vdl}>
                                                    <div className={`d-flex align-items-center ${styles.vdlHeader}`}>
                                                        <SelectAllCheckBox
                                                            onClearAll={onClearAllDivisionSelections(formApi)}
                                                            onSelectAll={onSelectAllDivisions(formApi)}
                                                            areSomeSelected={newSelectedDivisions.length > 0}
                                                            data-testid="day-planner.add-divisions.select-all"
                                                        />
                                                        {canCombineDivisions && (
                                                            <button
                                                                className={`btn btn-flex ${styles.combineDivisions} p0`}
                                                                onClick={combineSelectedDivisions(formApi)}
                                                                type="button"
                                                                data-testid="day-planner.add-divisions.combine-btn"
                                                            >
                                                                Combine divisions
                                                            </button>
                                                        )}
                                                        {!canCombineDivisions && (
                                                            <>
                                                                <div className={styles.priority} data-testid="day-planner.add-divisions.col-priority">
                                                                    Priority
                                                                </div>
                                                                <div className={styles.startTime} data-testid="day-planner.add-divisions.col-start-time">
                                                                    Start time
                                                                </div>
                                                                <div className={styles.divisions} data-testid="day-planner.add-divisions.col-divisions">
                                                                    {dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator ? 'Divisions' : 'Draws'}
                                                                </div>
                                                                <div className={styles.date} data-testid="day-planner.add-divisions.col-date">
                                                                    Date
                                                                </div>
                                                                <div className={styles.format} data-testid="day-planner.add-divisions.col-format">
                                                                    Format
                                                                </div>
                                                                <div className={styles.entrants} data-testid="day-planner.add-divisions.col-entrants">
                                                                    Players/Teams
                                                                </div>
                                                                <div className={styles.maxCourts} data-testid="day-planner.add-divisions.col-max-courts">
                                                                    Max courts
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    {values.virtualDraws?.map((virtualDraw, i) => {
                                                        const disable = formApi.getValue(['virtualDraws', i, 'disableDrawLevelChanges']);
                                                        const key = genRowKey(virtualDraw, i);
                                                        const rowTestId = `day-planner.add-divisions.row-${key}`;

                                                        return (
                                                            <div
                                                                key={key}
                                                                className={`d-flex align-items-center ${styles.vdlListItem}`}
                                                                data-testid={rowTestId}
                                                            >
                                                                <CheckBox
                                                                    id={`virtualDraws${i}selected`}
                                                                    field={['virtualDraws', i, 'selected']}
                                                                    formGroupCssClass={`mb0 ${styles.selected}`}
                                                                    disabled={disable}
                                                                    data-testid={`${rowTestId}.selected-checkbox`}
                                                                />
                                                                <Input
                                                                    id={`virtualDraws${i}priority`}
                                                                    field={['virtualDraws', i, 'priority']}
                                                                    type="number"
                                                                    min={1}
                                                                    formGroupCssClass={`mb0 ${styles.priority}`}
                                                                    themeV3
                                                                    data-testid={`${rowTestId}.priority-input`}
                                                                />
                                                                <Select
                                                                    id={`virtualDraws${i}startTime`}
                                                                    field={['virtualDraws', i, 'startTime']}
                                                                    formGroupCssClass={`mb0 ${styles.startTime}`}
                                                                    options={startTimeOptions}
                                                                    attributeToShow="title"
                                                                    attributeToSave="value"
                                                                    hiddenDefault
                                                                    themeV3
                                                                    disabled={disable}
                                                                    data-testid={`${rowTestId}.start-time-select`}
                                                                />
                                                                <div className={styles.divisions} data-testid={`${rowTestId}.name`}>
                                                                    {virtualDraw.name}
                                                                </div>
                                                                <div className={styles.date} data-testid={`${rowTestId}.date`}>
                                                                    {virtualDraw.date ? moment(virtualDraw.date).format(dateFormat) : ''}
                                                                </div>
                                                                {dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator && (
                                                                    <Select
                                                                        id={`virtualDraws${i}scoreFormat`}
                                                                        type="number"
                                                                        field={['virtualDraws', i, 'eliminationTypeId']}
                                                                        formGroupCssClass={`mb0 ${styles.format}`}
                                                                        options={scoreFormatOptions}
                                                                        attributeToShow="label"
                                                                        attributeToSave="eliminationTypeId"
                                                                        hiddenDefault
                                                                        themeV3
                                                                        disabled={disable}
                                                                        onChange={onChangeScoreFormatOptionId(formApi, i)}
                                                                        data-testid={`${rowTestId}.format-select`}
                                                                    />
                                                                )}
                                                                {dayPlanner.viewState.primaryView === primaryViews.DraftDraws && (
                                                                    <div className={styles.format} data-testid={`${rowTestId}.format`}>
                                                                        {getDrawEliminationTypeLabel(virtualDraw.eliminationTypeId)}
                                                                    </div>
                                                                )}
                                                                {dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator && (
                                                                    <Input
                                                                        id={`virtualDraws${i}entrants`}
                                                                        field={['virtualDraws', i, 'entrants']}
                                                                        type="number"
                                                                        min={1}
                                                                        formGroupCssClass={`mb0 ${styles.entrants}`}
                                                                        themeV3
                                                                        data-testid={`${rowTestId}.entrants-input`}
                                                                    />
                                                                )}
                                                                {dayPlanner.viewState.primaryView === primaryViews.DraftDraws && (
                                                                    <div className={styles.entrants} data-testid={`${rowTestId}.entrants`}>
                                                                        {virtualDraw.entrants}
                                                                    </div>
                                                                )}
                                                                <Select
                                                                    id={`virtualDraws${i}maxCourts`}
                                                                    type="number"
                                                                    field={['virtualDraws', i, 'maxCourts']}
                                                                    formGroupCssClass={`mb0 ${styles.maxCourts}`}
                                                                    options={maxCourtOptions}
                                                                    attributeToShow="label"
                                                                    attributeToSave="maxCourts"
                                                                    emptyOption="All"
                                                                    themeV3
                                                                    data-testid={`${rowTestId}.max-courts-select`}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                        {values.virtualDraws?.length === 0 && (
                                            <p className={`mb0 ${styles.notice}`} data-testid="day-planner.add-divisions.no-divisions-notice">
                                                No {dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator ? 'divisions' : 'draws'} are available for this day.
                                            </p>
                                        )}
                                    </div>
                                )}
                                {step === addDivisionSteps.DEFINE_SCORING_FORMATS && (
                                    <div className={`${styles.content} ${styles.addScoringFormatRules}`}>
                                        <p className={`mb0 ${styles.notice}`} data-testid="day-planner.add-divisions.scoring-formats-notice">
                                            In order to accurately plan your divisions, scoring formats need to be defined.
                                        </p>
                                        {values.scoringFormats.map((format, i) => {
                                            const playOffsForFormat = playOffDrawDetails.filter((detail) => {
                                                const isTypeValid = detail.drawTypeValue === format.value;

                                                if (isTypeValid && detail.needsExtraFieldsToCheck) {
                                                    const fieldMatchingCriteria = detail.extraFields.filter((extraField) => {
                                                        const valueToCheck = formApi.getValue(['scoringFormats', i, extraField.fieldToCheck]);
                                                        if (extraField.operation === 'greaterThanEqualTo') {
                                                            return parseInt(valueToCheck) >= extraField.value;
                                                        }

                                                        return valueToCheck === extraField.value;
                                                    });

                                                    return fieldMatchingCriteria.length > 0;
                                                }

                                                return isTypeValid;
                                            });

                                            const scoringTypeOverrideForFormat = scoringTypeOverrides.filter((override) => {
                                                const isTypeValid = override.drawTypeValue === format.value;

                                                if (isTypeValid && override.needsExtraFieldsToCheck) {
                                                    const fieldMatchingCriteria = override.extraFields.filter((extraField) => {
                                                        const valueToCheck = formApi.getValue(['scoringFormats', i, extraField.fieldToCheck]);
                                                        if (extraField.operation === 'greaterThanEqualTo') {
                                                            return parseInt(valueToCheck) >= extraField.value;
                                                        }

                                                        return valueToCheck === extraField.value;
                                                    });

                                                    return fieldMatchingCriteria.length > 0;
                                                }

                                                return isTypeValid;
                                            });

                                            const onChangeSplitGroupsCheck = (value) => {
                                                if (!value) {
                                                    formApi.reset(['scoringFormats', i, 'splitGroupsSize']);
                                                    formApi.reset(['scoringFormats', i, 'splitGroupsThreshold']);
                                                }
                                            };

                                            const onChangePlayOffField = (playOff, formatIndex, forceClear = false) => (value) => {
                                                if ((forceClear || !value) && playOff.fieldsToClear?.length > 0) {
                                                    playOff.fieldsToClear.forEach((fieldToClear) => {
                                                        if (fieldToClear.type === 'boolean') {
                                                            formApi.setValue(['scoringFormats', formatIndex, fieldToClear.name], false);
                                                        } else {
                                                            formApi.reset(['scoringFormats', formatIndex, fieldToClear.name]);
                                                        }
                                                    });
                                                }
                                            };

                                            const onChangeOverrideField = (override, formatIndex) => (value) => {
                                                if (!value) {
                                                    if (override.scoringFormatField) {
                                                        formApi.reset(['scoringFormats', formatIndex, override.scoringFormatField]);
                                                    }
                                                    if (override.allottedMatchTimeField) {
                                                        formApi.reset(['scoringFormats', formatIndex, override.allottedMatchTimeField]);
                                                    }
                                                }
                                            };

                                            const validateSplitField = (fApi, formatIndex) => (value) => {
                                                const isSplitSelected = fApi.getValue(['scoringFormats', formatIndex, 'shouldSplitIntoGroups']);
                                                return isSplitSelected ? required(value) : null;
                                            };

                                            const isFormatSelectedInVirtualDraws = values.virtualDraws?.filter((vd) => {
                                                return vd.selected && parseInt(vd.eliminationTypeId) === parseInt(format.eliminationTypeId);
                                            })?.length > 0;

                                            const formatTestId = `day-planner.add-divisions.format-${format.key}`;

                                            return (
                                                <div key={`format-${format.key}`} className={`${styles.formatRules} ${isFormatSelectedInVirtualDraws ? '' : 'd-none'}`} data-testid={formatTestId}>
                                                    <div className={styles.name} data-testid={`${formatTestId}.name`}>
                                                        {format.label}
                                                    </div>
                                                    <div className={styles.rulesSection}>
                                                        <div className={styles.splitGroupsSection}>
                                                            <CheckBox
                                                                id={`scoringFormats${i}shouldSplitIntoGroups`}
                                                                field={['scoringFormats', i, 'shouldSplitIntoGroups']}
                                                                formGroupCssClass={`mb0`}
                                                                onChange={onChangeSplitGroupsCheck}
                                                                data-testid={`${formatTestId}.split-groups-checkbox`}
                                                            />
                                                            <div data-testid={`${formatTestId}.split-into-label`}>
                                                                Split into
                                                            </div>
                                                            <Input
                                                                id={`scoringFormats${i}splitGroupsSize`}
                                                                field={['scoringFormats', i, 'splitGroupsSize']}
                                                                formGroupCssClass={`mb0 ${styles.groupInput}`}
                                                                themeV3
                                                                validate={validateSplitField(formApi, i)}
                                                                data-testid={`${formatTestId}.split-groups-size-input`}
                                                            />
                                                            <div data-testid={`${formatTestId}.split-threshold-label`}>
                                                                {`${format.value === divisionScoringFormatKeyMap.roundRobin ? 'groups(s)' : 'draw(s)'} if larger than`}
                                                            </div>
                                                            <Input
                                                                id={`scoringFormats${i}splitGroupsThreshold`}
                                                                field={['scoringFormats', i, 'splitGroupsThreshold']}
                                                                formGroupCssClass={`mb0 ${styles.groupInput}`}
                                                                themeV3
                                                                validate={validateSplitField(formApi, i)}
                                                                data-testid={`${formatTestId}.split-threshold-input`}
                                                            />
                                                        </div>
                                                        {format.value === divisionScoringFormatKeyMap.doubleElimination && (
                                                            <CheckBox
                                                                id={`scoringFormats${i}preventBackDrawWinnerComeAround`}
                                                                field={['scoringFormats', i, 'preventBackDrawWinnerComeAround']}
                                                                customLabel={'Prevent back draw winner(s) from moving to main draw'}
                                                                formGroupCssClass={`mb0`}
                                                                data-testid={`${formatTestId}.prevent-backdraw-checkbox`}
                                                            />
                                                        )}
                                                        {playOffsForFormat.length > 0 && (
                                                            <div className={`${styles.playOffsSection} ${styles.addMarginsForChild}`}>
                                                                {playOffsForFormat.map((playOff) => {
                                                                    if (playOff.fieldType === 'select') {
                                                                        return (
                                                                            <Select
                                                                                key={`playoff-field-${playOff.id}`}
                                                                                id={`scoringFormats${i}${playOff.fieldName}`}
                                                                                field={['scoringFormats', i, playOff.fieldName]}
                                                                                formGroupCssClass={`mb0`}
                                                                                label={playOff.fieldLabel}
                                                                                hiddenDefault
                                                                                options={playOff.fieldOptions}
                                                                                attributeToShow="label"
                                                                                attributeToSave="value"
                                                                                onChange={onChangePlayOffField(playOff, i, true)}
                                                                                themeV3
                                                                                data-testid={`${formatTestId}.playoff-${playOff.id}-select`}
                                                                            />
                                                                        );
                                                                    }

                                                                    if (playOff.fieldType === 'checkbox') {
                                                                        return (
                                                                            <CheckBox
                                                                                key={`playoff-field-${playOff.id}`}
                                                                                id={`scoringFormats${i}${playOff.fieldName}`}
                                                                                field={['scoringFormats', i, playOff.fieldName]}
                                                                                customLabel={playOff.fieldLabel}
                                                                                formGroupCssClass={`mb0`}
                                                                                onChange={onChangePlayOffField(playOff, i, false)}
                                                                                data-testid={`${formatTestId}.playoff-${playOff.id}-checkbox`}
                                                                            />
                                                                        );
                                                                    }

                                                                    return null;
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className={`${styles.scoringFormatsSection} ${styles.addMarginsForChild}`}>
                                                            <div className="d-flex align-items-start">
                                                                <div className="flex-1">
                                                                    <Select
                                                                        id={`scoringFormats${i}defaultScoringTypeId`}
                                                                        field={['scoringFormats', i, 'defaultScoringTypeId']}
                                                                        label="Default scoring type"
                                                                        options={getSelectableScoringFormats(values)}
                                                                        themeV3
                                                                        formGroupCssClass={`mb0`}
                                                                        validate={required}
                                                                        onChange={onScoringFormatChange(formApi, i, 'defaultAllottedMatchTime')}
                                                                        data-testid={`${formatTestId}.default-scoring-type-select`}
                                                                    />
                                                                </div>
                                                                <div className="flex-1 ml16">
                                                                    <Select
                                                                        id={`scoringFormats${i}defaultAllottedMatchTime`}
                                                                        field={['scoringFormats', i, 'defaultAllottedMatchTime']}
                                                                        label="Default allotted time"
                                                                        options={allowedMatchTimes}
                                                                        themeV3
                                                                        formGroupCssClass={`mb0`}
                                                                        validate={required}
                                                                        data-testid={`${formatTestId}.default-allotted-time-select`}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {scoringTypeOverrideForFormat.map((override) => {
                                                                return (
                                                                    <>
                                                                        <CheckBox
                                                                            id={`scoringFormats${i}${override.field}`}
                                                                            field={['scoringFormats', i, override.field]}
                                                                            customLabel={override.label}
                                                                            formGroupCssClass={`mb0`}
                                                                            onChange={onChangeOverrideField(override, i)}
                                                                            data-testid={`${formatTestId}.override-${override.field}-checkbox`}
                                                                        />
                                                                        {formApi.getValue(['scoringFormats', i, override.field]) && (
                                                                            <div className="d-flex align-items-start">
                                                                                <div className="flex-1">
                                                                                    <Select
                                                                                        id={`scoringFormats${i}${override.scoringFormatField}`}
                                                                                        field={['scoringFormats', i, override.scoringFormatField]}
                                                                                        label="Draw scoring type"
                                                                                        options={getSelectableScoringFormats(values)}
                                                                                        themeV3
                                                                                        formGroupCssClass={`mb0`}
                                                                                        validate={required}
                                                                                        onChange={onScoringFormatChange(formApi, i, override.allottedMatchTimeField)}
                                                                                        data-testid={`${formatTestId}.override-${override.field}-scoring-type-select`}
                                                                                    />
                                                                                </div>
                                                                                <div className="flex-1 ml16">
                                                                                    <Select
                                                                                        id={`scoringFormats${i}${override.allottedMatchTimeField}`}
                                                                                        field={['scoringFormats', i, override.allottedMatchTimeField]}
                                                                                        label="Draw allotted time"
                                                                                        options={allowedMatchTimes}
                                                                                        themeV3
                                                                                        formGroupCssClass={`mb0`}
                                                                                        validate={required}
                                                                                        data-testid={`${formatTestId}.override-${override.field}-allotted-time-select`}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                <div className={`${styles.footer} d-flex align-items-center`}>
                                    <button
                                        className={`btn btn-primary ${styles.submitButton} mr8`}
                                        type="button"
                                        onClick={onCancelClick}
                                        data-testid="day-planner.add-divisions.cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                    <div className="flex-1" />
                                    {!areValidScoreFormatPriorities && (
                                        <div className={styles.invalidFeedback} data-testid="day-planner.add-divisions.invalid-priorities">Priorities are invalid</div>
                                    )}
                                    <div className="flex-1" />
                                    {(dayPlanner.viewState.primaryView === primaryViews.CapacitySimulator &&
                                     step === addDivisionSteps.SELECT_DIVISIONS) && (
                                     <button
                                         className={`btn btn-primary ${styles.submitButton}`}
                                         type="button"
                                         onClick={onClickAddFormatRules}
                                         disabled={!canContinueFromSelections}
                                         data-testid="day-planner.add-divisions.add-format-rules-btn"
                                     >
                                            Add format rules
                                     </button>
                                    )}
                                    {(dayPlanner.viewState.primaryView === primaryViews.DraftDraws ||
                                     step === addDivisionSteps.DEFINE_SCORING_FORMATS) && (
                                     <button
                                         className={`btn btn-primary ${styles.submitButton}`}
                                         type="submit"
                                         disabled={!canContinueFromSelections}
                                         data-testid="day-planner.add-divisions.submit-btn"
                                     >
                                            Submit
                                     </button>
                                    )}
                                </div>
                            </DayPlannerSidePanel>
                        </form>
                        {combinedDivisionState.needsConfirmation && (
                            <Popup
                                className="popup-sm"
                                popUpClassName={styles.confirmPopup}
                                onCloseClick={onCloseConfirmCombine}
                                closeOnClickOutside={false}
                                config={{
                                    fullScreen: false,
                                    noPadding: true,
                                    scrollable: false,
                                    showCloseIcon: false,
                                }}
                                data-testid="modal.day-planner.confirm-combine-divisions"
                            >
                                <p className={styles.description} data-testid="modal.day-planner.confirm-combine-divisions.description">
                                    What format do you want to use?
                                </p>
                                <Select
                                    options={scoreFormatOptions}
                                    value={combinedDivisionState.eliminationTypeId}
                                    attributeToShow="name"
                                    attributeToSave="formatOptionsId"
                                    hiddenDefault
                                    onChange={onChangeCombinedFormatOption}
                                    controlled
                                    themeV3
                                    data-testid="modal.day-planner.confirm-combine-divisions.format-select"
                                />
                                <div className="d-flex mt16">
                                    <button
                                        className="btn btn-default flex-1 mr8"
                                        type="button"
                                        onClick={onCloseConfirmCombine}
                                        data-testid="modal.day-planner.confirm-combine-divisions.cancel-btn"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-primary flex-1"
                                        type="button"
                                        onClick={onConfirmCombine(formApi)}
                                        data-testid="modal.day-planner.confirm-combine-divisions.confirm-btn"
                                    >
                                        Save
                                    </button>
                                </div>
                            </Popup>
                        )}
                    </>
                );
            }}
        </Form>
    );
};

AddDivisionsSidePanel.propTypes = {
    className: PropTypes.string,
    closeSidePanel: PropTypes.func.isRequired,
    eventId: PropTypes.number.isRequired,
    sportTypeId: PropTypes.number,
    eventTypeId: PropTypes.number,
    eventSchedule: PropTypes.objectOf(any).isRequired,
    eventAllottedMatchTimes: PropTypes.arrayOf(any),
};

AddDivisionsSidePanel.defaultProps = {
    className: '',
    sportTypeId: sportTypeIds.TENNIS,
    eventTypeId: EventTypes.TOURNAMENT,
    eventAllottedMatchTimes: [],
};

export default AddDivisionsSidePanel;
